import React, { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  X, Send, Clock, Check, CheckCheck, MessageSquare,
  FileText, Loader2, ChevronDown, Phone, Bot, UserRound
} from "lucide-react";
import { waGetMessages, waSendMessage, waSendFollowUp, waGetTemplates } from "./api";

const API = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_ICONS = {
  sent: <Check className="w-3 h-3 wa-status-sent" />,
  delivered: <CheckCheck className="w-3 h-3 wa-status-delivered" />,
  read: <CheckCheck className="w-3 h-3 wa-status-read" />,
  received: null,
  failed: <X className="w-3 h-3 wa-status-failed" />,
};

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function groupByDate(messages) {
  const groups = [];
  let currentDate = "";
  for (const msg of messages) {
    const date = formatDate(msg.wa_timestamp);
    if (date !== currentDate) {
      currentDate = date;
      groups.push({ type: "date", date });
    }
    groups.push({ type: "message", ...msg });
  }
  return groups;
}

export default function WhatsAppChat({ lead, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [aiPaused, setAiPaused] = useState(false);
  const [takeoverLoading, setTakeoverLoading] = useState(false);
  const scrollRef = useRef(null);

  const phone = lead.whatsappNumber || "";

  useEffect(() => {
    loadMessages();
    loadTakeoverStatus();
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, [lead.id]);

  const loadTakeoverStatus = async () => {
    try {
      const resp = await fetch(`${API}/api/demo/whatsapp/takeover/${lead.id}`);
      const data = await resp.json();
      setAiPaused(data.aiPaused || false);
    } catch (e) {}
  };

  const toggleTakeover = async () => {
    setTakeoverLoading(true);
    try {
      const action = aiPaused ? "release" : "takeover";
      await fetch(`${API}/api/demo/whatsapp/takeover/${lead.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      setAiPaused(!aiPaused);
    } catch (e) {
      setError(e.message);
    } finally {
      setTakeoverLoading(false);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const loadMessages = async () => {
    try {
      const data = await waGetMessages(lead.id);
      setMessages(data.messages || []);
    } catch (e) {
      console.error("Failed to load messages:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!text.trim() || !phone) return;
    setSending(true);
    setError(null);
    try {
      await waSendMessage(phone, text.trim(), lead.id);
      setText("");
      await loadMessages();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendTemplate = async (templateName) => {
    if (!phone) return;
    setSendingTemplate(templateName);
    setError(null);
    try {
      await waSendFollowUp(lead.id, templateName);
      setShowTemplates(false);
      await loadMessages();
    } catch (e) {
      setError(e.message);
    } finally {
      setSendingTemplate(null);
    }
  };

  const loadTemplates = async () => {
    if (templates.length > 0) {
      setShowTemplates(!showTemplates);
      return;
    }
    setTemplatesLoading(true);
    try {
      const data = await waGetTemplates();
      setTemplates(data.templates || []);
      setShowTemplates(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const grouped = groupByDate(messages);

  return (
    <div className="wa-chat-panel" data-testid="wa-chat-panel">
      {/* Header */}
      <div className="wa-chat-header" data-testid="wa-chat-header">
        <div className="wa-chat-header-avatar">
          {lead.clientName?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="wa-chat-header-info">
          <span className="wa-chat-header-name">
            {lead.clientName} {lead.clientLastName}
          </span>
          <span className="wa-chat-header-phone">
            <Phone className="w-3 h-3" /> {phone || "No number"}
          </span>
        </div>
        <div className="wa-chat-header-actions">
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleTakeover}
            disabled={takeoverLoading}
            className={`wa-takeover-btn ${aiPaused ? "wa-takeover-manual" : "wa-takeover-ai"}`}
            data-testid="wa-takeover-btn"
            title={aiPaused ? "AI paused — click to resume AI" : "AI active — click to take over manually"}
          >
            {takeoverLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : aiPaused ? <><UserRound className="w-3.5 h-3.5" /> Manual</> : <><Bot className="w-3.5 h-3.5" /> AI</>}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={loadTemplates}
            disabled={templatesLoading}
            className="wa-template-btn"
            data-testid="wa-templates-btn"
          >
            {templatesLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Templates
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="wa-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Template Picker */}
      {showTemplates && (
        <div className="wa-template-picker" data-testid="wa-template-picker">
          <div className="wa-template-picker-header">
            <span>Approved Templates</span>
            <button onClick={() => setShowTemplates(false)}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {templates.length === 0 ? (
            <div className="wa-template-empty">No approved templates found</div>
          ) : (
            templates.filter(t => t.status === "APPROVED").map((t) => (
              <div key={`${t.name}-${t.language}`} className="wa-template-item" data-testid={`wa-template-${t.name}`}>
                <div className="wa-template-item-info">
                  <span className="wa-template-item-name">{t.name}</span>
                  <span className="wa-template-item-cat">{t.category} &middot; {t.language}</span>
                  {t.components?.find(c => c.type === "BODY") && (
                    <span className="wa-template-item-preview">
                      {t.components.find(c => c.type === "BODY").text?.slice(0, 80)}...
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleSendTemplate(t.name)}
                  disabled={sendingTemplate === t.name}
                  className="wa-template-send-btn"
                  data-testid={`wa-send-template-${t.name}`}
                >
                  {sendingTemplate === t.name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Messages area */}
      <div className="wa-chat-messages" ref={scrollRef} data-testid="wa-chat-messages">
        {loading ? (
          <div className="wa-chat-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading messages...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="wa-chat-empty" data-testid="wa-chat-empty">
            <MessageSquare className="w-8 h-8" />
            <span>No messages yet</span>
            <span className="wa-chat-empty-hint">
              Send a template message to start the conversation
            </span>
          </div>
        ) : (
          grouped.map((item, i) =>
            item.type === "date" ? (
              <div key={`date-${i}`} className="wa-date-divider">
                <span>{item.date}</span>
              </div>
            ) : (
              <div
                key={item.id || `msg-${i}`}
                className={`wa-bubble ${item.direction === "outgoing" ? "wa-bubble-out" : "wa-bubble-in"}`}
                data-testid={`wa-msg-${item.id}`}
              >
                {item.template_name && (
                  <span className="wa-bubble-template-badge">
                    <FileText className="w-3 h-3" /> {item.template_name}
                  </span>
                )}
                <span className="wa-bubble-text">{item.body}</span>
                <span className="wa-bubble-meta">
                  <Clock className="w-2.5 h-2.5" />
                  {formatTime(item.wa_timestamp)}
                  {item.direction === "outgoing" && STATUS_ICONS[item.status]}
                </span>
              </div>
            )
          )
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="wa-chat-error" data-testid="wa-chat-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Input area */}
      <div className="wa-chat-input" data-testid="wa-chat-input">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={phone ? "Type a message..." : "No WhatsApp number on this lead"}
          disabled={!phone || sending}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
          className="wa-chat-input-field"
          data-testid="wa-message-input"
        />
        <Button
          onClick={handleSend}
          disabled={!text.trim() || !phone || sending}
          className="wa-send-btn"
          data-testid="wa-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
